import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { researchAPI, profilesAPI } from '../services/api';
import { useToast } from './Toast';
import ResearchWizard from './ResearchWizard';
import '../styles/WorkspaceChrome.css';
import './ResearchReports.css';

function reportTitleSnippet(report) {
  const ideas = report.article_ideas;
  if (ideas && ideas[0]?.title) {
    const t = String(ideas[0].title);
    return t.length > 80 ? `${t.slice(0, 77)}…` : t;
  }
  if (report.run_context?.title?.trim()) return report.run_context.title.trim();
  return '—';
}

function profileLabel(report, profiles) {
  const rc = report.run_context || {};
  if (rc.kind === 'content_cluster' || rc.foundation === 'existing_content') {
    const title = rc.source?.title;
    return title ? `From: ${title}` : 'From existing content';
  }
  if (report.profile_id != null) {
    const p = profiles.find((x) => x.id === report.profile_id);
    return p ? p.name : `Saved #${report.profile_id}`;
  }
  if (rc.brief_name) return rc.brief_name;
  if (rc.title?.trim()) return rc.title.trim();
  return 'Research brief';
}

function reportFoundationPill(report) {
  const rc = report.run_context || {};
  if (rc.kind === 'content_cluster' || rc.foundation === 'existing_content') {
    return { label: 'Cluster', className: 'rr-pill-cluster' };
  }
  if (rc.kind === 'focused') {
    return { label: 'Focused', className: 'rr-pill-focused' };
  }
  if (rc.foundation === 'brief' || rc.kind === 'general') {
    return { label: 'Brief', className: 'rr-pill-brief' };
  }
  return null;
}

/**
 * Research reports table: main inbox or archived-only (`archiveMode`).
 */
function ResearchReportsListPage({ archiveMode = false }) {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const wizardManage = searchParams.get('manage') === 'saved';
  const [reports, setReports] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (selectedProfile) params.profile_id = selectedProfile;
      if (archiveMode) params.archived_only = true;
      const [reportsRes, profilesRes] = await Promise.all([
        researchAPI.getReports(params),
        profilesAPI.getProfiles(),
      ]);
      setReports(reportsRes.data);
      setProfiles(profilesRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(
        `Failed to load research reports: ${err.response?.data?.error || err.message || 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  }, [selectedProfile, archiveMode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleArchiveToggle = async (e, report) => {
    e.stopPropagation();
    const next = !(report.workspace_archived === true);
    try {
      await researchAPI.patchReport(report.id, { workspace_archived: next });
      showToast(next ? 'Report archived' : 'Report restored', 'success');
      loadData();
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Update failed',
        'error'
      );
    }
  };

  const handleDelete = async (e, reportId) => {
    e.stopPropagation();
    if (!window.confirm(`Delete Report #${reportId}? This cannot be undone.`)) return;
    try {
      await researchAPI.deleteReport(reportId);
      showToast('Report deleted successfully', 'success');
      loadData();
    } catch (err) {
      showToast(
        `Failed to delete report: ${err.response?.data?.error || err.message || 'Unknown error'}`,
        'error'
      );
    }
  };

  const base = '/research/reports';

  if (loading) {
    return <div className="loading">Loading research reports...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="rr-workspace rr-workspace--list">
      {!archiveMode && (
        <ResearchWizard
          initialView={wizardManage ? 'manage' : 'wizard'}
          onReportReady={(reportId) => {
            loadData();
            navigate(`${base}/${reportId}`);
          }}
        />
      )}

      <div className="rr-header">
        <div>
          <h2>{archiveMode ? 'Archived research reports' : 'Research reports'}</h2>
          <p className="rr-sub">
            {archiveMode ? (
              <>
                Archived reports are hidden from the main inbox.{' '}
                <Link to={base}>Back to active reports</Link>
              </>
            ) : (
              <>
                Approve <strong>angles</strong> to move them into draft candidates. Open a row for the
                full report.
              </>
            )}
          </p>
        </div>
        <div className="rr-header-right">
          <div className="rr-filters">
            <select
              value={selectedProfile}
              onChange={(e) => setSelectedProfile(e.target.value)}
              className="rr-select"
            >
              <option value="">All saved research</option>
              {profiles.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.name}
                </option>
              ))}
            </select>
          </div>
          {!archiveMode && (
            <Link to={`${base}/archive`} className="rr-link-archive">
              View archive
            </Link>
          )}
        </div>
      </div>

      <div className="rr-table-wrap">
        {reports.length === 0 ? (
          <div className="rr-empty">No research reports found</div>
        ) : (
          <table className="rr-table">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Source</th>
                <th scope="col">Lead angle / title</th>
                <th scope="col" className="rr-col-narrow">
                  Angles
                </th>
                <th scope="col">Status</th>
                <th scope="col" className="rr-col-actions">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                  <tr
                    key={report.id}
                    className="rr-table-row-click"
                    onClick={() => navigate(`${base}/${report.id}`)}
                  >
                    <td>
                      {report.report_date
                        ? new Date(report.report_date).toLocaleDateString()
                        : report.created_at
                          ? new Date(report.created_at).toLocaleDateString()
                          : '—'}
                    </td>
                    <td>
                      <span className="rr-table-source">{profileLabel(report, profiles)}</span>
                      {(() => {
                        const pill = reportFoundationPill(report);
                        return pill ? (
                          <span className={`rr-pill ${pill.className} rr-pill-inline`} title={pill.label}>
                            {pill.label}
                          </span>
                        ) : null;
                      })()}
                    </td>
                    <td className="rr-table-snippet">{reportTitleSnippet(report)}</td>
                    <td>{report.article_ideas?.length ?? 0}</td>
                    <td>
                      <span className={`rr-status rr-status-${report.status}`}>{report.status}</span>
                    </td>
                    <td className="rr-table-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="rr-btn-sm rr-btn-table"
                        onClick={(e) => handleArchiveToggle(e, report)}
                      >
                        {report.workspace_archived === true ? 'Restore' : 'Archive'}
                      </button>
                      <button
                        type="button"
                        className="rr-btn-sm rr-btn-table rr-btn-delete"
                        onClick={(e) => handleDelete(e, report.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ResearchReportsListPage;
